import {
  BriefcaseIcon,
  CalendarClockIcon,
  ClipboardCheckIcon,
  FingerprintIcon,
  FolderOpenIcon,
  KeyRoundIcon,
  type LucideIcon,
  TreePalmIcon,
  UserRoundIcon,
  WalletIcon,
} from 'lucide-react'

/** The onboarding wizard's steps, in order (spec D2). */
export type OnboardingStepId =
  | 'personal'
  | 'job'
  | 'pay'
  | 'leave'
  | 'schedule'
  | 'attendance'
  | 'documents'
  | 'access'
  | 'review'

export interface OnboardingStep {
  id: OnboardingStepId
  icon: LucideIcon
  /** An optional step offers Skip, which moves on and saves nothing (D4). */
  optional: boolean
  /** Shown only to a caller who can read and save it (D22); absent means everyone. */
  allowed?: (can: (grant: string) => boolean) => boolean
}

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  { id: 'personal', icon: UserRoundIcon, optional: false },
  { id: 'job', icon: BriefcaseIcon, optional: false },
  {
    id: 'pay',
    icon: WalletIcon,
    optional: true,
    // Reads the financial group; saves it, or pay components, in their dialogs.
    allowed: (can) =>
      can('employee_financial.view') &&
      (can('employee_financial.edit') || can('payroll_component.edit')),
  },
  { id: 'leave', icon: TreePalmIcon, optional: true, allowed: (can) => can('leave.view') },
  // Saved with the attendance edit grant (the employee's schedule).
  {
    id: 'schedule',
    icon: CalendarClockIcon,
    optional: true,
    allowed: (can) => can('attendance.edit'),
  },
  {
    id: 'attendance',
    icon: FingerprintIcon,
    optional: true,
    // Reads the employee's policies; saves the policy with the same grant's edit.
    allowed: (can) => can('attendance.view') && can('attendance.edit'),
  },
  {
    id: 'documents',
    icon: FolderOpenIcon,
    optional: true,
    allowed: (can) => can('employee_document.view') || can('asset.view'),
  },
  // Reads the employee's pending invitations; the invite itself needs user.create.
  { id: 'access', icon: KeyRoundIcon, optional: true, allowed: (can) => can('user.view') },
  { id: 'review', icon: ClipboardCheckIcon, optional: false },
]

const ALL = ONBOARDING_STEPS.map((step) => step.id)

/** The steps this caller sees, in order. */
export function visibleSteps(can: (grant: string) => boolean): OnboardingStep[] {
  return ONBOARDING_STEPS.filter((step) => !step.allowed || step.allowed(can))
}

/** The step a `?step=` value names, or undefined for anything else. */
export function parseOnboardingStep(value: unknown): OnboardingStepId | undefined {
  return ALL.find((id) => id === value)
}

export function stepIndex(id: OnboardingStepId, ids: readonly OnboardingStepId[] = ALL): number {
  return ids.indexOf(id)
}

/** The step after `id`; the last step stays where it is. */
export function stepAfter(
  id: OnboardingStepId,
  ids: readonly OnboardingStepId[] = ALL,
): OnboardingStepId {
  return ids[Math.min(stepIndex(id, ids) + 1, ids.length - 1)] ?? id
}

/** The step before `id`; the first step stays where it is. */
export function stepBefore(
  id: OnboardingStepId,
  ids: readonly OnboardingStepId[] = ALL,
): OnboardingStepId {
  return ids[Math.max(stepIndex(id, ids) - 1, 0)] ?? id
}

/**
 * Where "Finish setup" resumes (D21): the first step that is not Done, else
 * Review. Personal is Done once the employee exists (its names are saved).
 */
export function firstIncompleteStep(
  incomplete: ReadonlySet<OnboardingStepId>,
  ids: readonly OnboardingStepId[] = ALL,
): OnboardingStepId {
  return ids.find((id) => id !== 'review' && incomplete.has(id)) ?? 'review'
}
